import { useTranslation } from "react-i18next";
import { Controller, useFormContext } from "react-hook-form";
import {
  TextField,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  FormHelperText,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";

import { salaryRateList } from "../../../../../enum/jobEnums";
import { CreateTalentJob } from "../../../../../types/createJobSchema";
import { currencyList } from "../../../../../../../enums/currencies.enum";
import CreateJobAccordion from "../../../../../components/CreateJobAccordion";

const CompensationAndBenefits = () => {
  const { t } = useTranslation("goHire", { keyPrefix: "createJobPage.jobDetails" });
  const { t: tCommon } = useTranslation("common", { keyPrefix: "currency" });
  const {
    control,
    formState: { errors },
  } = useFormContext<CreateTalentJob>();

  const hasError = Boolean(errors.salaryAmount || errors.salaryCurrency || errors.salaryPer || errors.isConfidential);

  return (
    <CreateJobAccordion title={t("compensationBenefits")} error={hasError}>
      <Controller
        name="salaryAmount"
        control={control}
        render={({ field: amountField, fieldState: { error: salaryAmountError } }) => (
          <>
            <FormLabel sx={styles.formChildLabel} error={!!salaryAmountError}>
              {t("salary")}
            </FormLabel>
            <Box sx={styles.currencySelectSubMenuContainer}>
              <Controller
                name="salaryCurrency"
                control={control}
                render={({ field: currencyField }) => (
                  <Select
                    {...currencyField}
                    value={currencyField.value}
                    sx={styles.currencySelectSubMenu}
                    onChange={(e) => currencyField.onChange(e.target.value)}
                  >
                    {currencyList.map((currency: string) => {
                      return (
                        <MenuItem key={currency} value={currency}>
                          {tCommon(currency)}
                        </MenuItem>
                      );
                    })}
                  </Select>
                )}
              />
              <TextField {...amountField} fullWidth sx={styles.averageCostTextField} />
            </Box>
            {salaryAmountError && <FormHelperText error>{t(salaryAmountError?.message)}</FormHelperText>}
          </>
        )}
      />
      <Controller
        name="salaryPer"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <FormControl fullWidth component="fieldset" error={!!error}>
            <FormLabel error={!!error}>{t("salaryPer")}</FormLabel>
            <RadioGroup {...field} sx={styles.salaryPerRadioGroup}>
              {salaryRateList.map((rate) => (
                <FormControlLabel key={String(rate)} value={rate} control={<Radio size="small" />} label={t(rate)} />
              ))}
            </RadioGroup>
            {error?.message && <FormHelperText error>{t(error.message)}</FormHelperText>}
          </FormControl>
        )}
      />

      <Controller
        name="isConfidential"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <FormControl fullWidth component="fieldset" error={!!error}>
            <FormControlLabel {...field} control={<Checkbox size="small" />} label={t("markSalaryAsConfidential")} />
            {error?.message && <FormHelperText error>{t(error.message)}</FormHelperText>}
          </FormControl>
        )}
      />
    </CreateJobAccordion>
  );
};

export default CompensationAndBenefits;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  salaryPerRadioGroup: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  formLabel: {
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  fixedCostCard: {
    flex: 1,
    border: "1px solid var(--Gray-200, #E4E7EC)",
    borderRadius: "12px",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  fixedCostContainer: {
    display: "flex",
    flexDirection: " column",
    alignItems: "flex-start",
    gap: "24px",
  },
  fixedCostTitle: {
    fontFamily: "Montserrat",
    color: "var(--Gray-700, #344054)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "24px",
  },
  fixedCostDescription: {
    fontFamily: "Montserrat",
    color: "var(--Gray-600, #475467)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "24px",
  },
  formControlLabel: {
    margin: 0,
    gap: "12px",
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "24px",
  },
  formChildLabel: {
    color: "var(--Dark---800, #414141)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
  },
  finalServiceCostContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    borderRadius: "12px",
    border: "1px solid #EAECF0",
    background: "var(--Green---25, #E7EFED)",
    padding: "16px",
  },
  finalServiceCostBox: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  finalServiceCostRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  finalServiceCostTitle: {
    fontFamily: "Montserrat",
    color: "var(--Gray-900, #1A1C21)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
  },
  finalServiceCostDescription: {
    fontFamily: "Montserrat",
    color: "var(--Dark---600, #707070)",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",
  },
  serviceCostTitle: {
    fontFamily: "Montserrat",
    color: "var(--Gray-900, #1A1C21)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "20px",
  },
  finalServiceCostValue: {
    fontFamily: "Montserrat",
    color: "var(--Gray-900, #1A1C21)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  fixedCostCardTitleWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  serviceAmountWarningBox: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    borderRadius: "8px",
    background: "var(--Brand-50, #E7FFE4)",
    padding: "6px 8px",
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",
  },
  fixedCostWarning: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",
  },
  currencySelectSubMenu: {
    borderTopRightRadius: "0 !important",
    borderBottomRightRadius: "0 !important",
    borderTop: "none !important",
    borderBottom: "none !important",
    borderLeft: "none !important",
    borderRight: "1px solid var(--Gray-300, #D0D5DD)",
    paddingRight: "0",
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none !important",
    },
    "& .MuiInputBase-root.MuiOutlinedInput-root": {
      border: "none !important",
    },
    "& .MuiSelect-select.MuiSelect-outlined.MuiInputBase-input": {
      padding: "8px 16px 8px 8px",
    },
  },
  currencySelectSubMenuContainer: {
    display: "flex",
    border: "1px solid var(--Gray-300, #D0D5DD)",
    borderRadius: "33px",
    width: "100%",
  },
  averageCostTextField: {
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none !important",
    },
    "& .MuiInputBase-root": {
      border: "none !important",
    },
  },
  fixedCostSubContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  fixedCostLabel: {
    color: "var(--Gray-700, #344054)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "20px",
  },
};
